import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import GroupPicker from '@/components/GroupPicker.vue'
import { state, getActiveGroup, getActiveGroupId, getRecentGroupIds, switchGroup } from '@/util/activeGroup'
import type { GroupSummary } from '@/util/activeGroup'
import { SEARCH_THRESHOLD } from '@/util/groupPickerFilter'

// Below this width the theme shows the sidebar as an off-canvas drawer (hamburger)

const __default__ = { name: 'group-switcher' }

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  variant: {
    type: String,
    required: true,
    validator: (v: string) => ['sidebar', 'palette-host'].includes(v),
  },
},
  setup(__props) {

const props = __props

// Location switcher. Variants:
// - 'sidebar': the sidebar's brand row (location image, company label, location name).
//   With fewer than 9 locations a click swaps the menu for the location list (option A);
//   with 9 or more, or from the folded sidebar, it opens the search palette (option C).
// - 'palette-host': the palette itself plus the Ctrl K / Cmd K shortcut, mounted once in
//   DashboardLayout so it sits above the sidebar, navbar and drawers.
const SIDEBAR_DRAWER_MAX = 1200



const proxy = getCurrentInstance()!.proxy as any

// The active location never changes without a full page load, so read it once.
const active = getActiveGroup() || {}
const name: string = active.name || ''
const currentId = getActiveGroupId()
const PLACEHOLDER_IMG = 'https://console.offthecouch.io/img/placeholder.jpg'
const groupImg: string = active.img && active.img !== PLACEHOLDER_IMG ? active.img : ''
const initials: string = (name.split(/\s+/).filter(Boolean).slice(0, 2).map((w: string) => w.charAt(0)).join('') || '?').toUpperCase()

function readJson(key: string): any {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null')
  } catch (e) {
    return null
  }
}
const companyName: string = (readJson('company') || {}).name || ''
const permissions: string[] = readJson('permissions') || []
const canManage = Array.isArray(permissions) && permissions.includes('company_settings_configure')
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '')
const shortcutLabel = isMac ? 'Cmd K' : 'Ctrl K'

const groups = computed(() => state.groups)
const canSwitch = computed(() => state.groups.length > 1)
const switching = computed(() => state.switching)
const switchingToName = computed(() => state.switchingToName)
const paletteOpen = computed(() => state.paletteOpen)

const trigger = ref<HTMLElement | null>(null)
const dropdown = ref<HTMLElement | null>(null)
const dropdownOpen = ref(false)
const recentIds = ref<number[]>([])
const isPhone = ref(typeof window !== 'undefined' && window.innerWidth < SIDEBAR_DRAWER_MAX)

watch([dropdownOpen, paletteOpen], ([d, p]) => {
  if (d || p) recentIds.value = getRecentGroupIds()
})

// While the dropdown is open the location list takes the menu's place in the sidebar
// (_location-picker.scss hides the menu under .sidebar--picking).
watch(dropdownOpen, (o) => {
  const sidebar = trigger.value && trigger.value.closest('.sidebar')
  if (sidebar) sidebar.classList.toggle('sidebar--picking', o)
})

// The palette locks the page behind it and hands focus back on close. The previous
// overflow is restored, not cleared, so a drawer open underneath keeps its own lock.
let prevOverflow = ''
let prevFocus: HTMLElement | null = null
watch(paletteOpen, async (o) => {
  if (props.variant !== 'palette-host') return
  isPhone.value = window.innerWidth < SIDEBAR_DRAWER_MAX
  if (o) {
    prevOverflow = document.body.style.overflow
    prevFocus = document.activeElement as HTMLElement | null
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = prevOverflow
    await nextTick()
    if (prevFocus && document.contains(prevFocus) && typeof prevFocus.focus === 'function') prevFocus.focus()
    prevFocus = null
  }
})

function openPalette(): void {
  if (!canSwitch.value || state.switching) return
  dropdownOpen.value = false
  state.paletteOpen = true
}

function closePalette(): void {
  state.paletteOpen = false
}

async function closeDropdown(): Promise<void> {
  dropdownOpen.value = false
  await nextTick()
  if (trigger.value) trigger.value.focus()
}

function onBrandClick(): void {
  if (!canSwitch.value || state.switching) return
  const phone = window.innerWidth < SIDEBAR_DRAWER_MAX
  const folded = !phone && proxy.$sidebar && proxy.$sidebar.isMinimized
  if (state.groups.length >= SEARCH_THRESHOLD || folded) {
    if (phone && proxy.$sidebar && proxy.$sidebar.showSidebar) {
      // let the drawer finish sliding away before the palette opens over the page
      proxy.$sidebar.displaySidebar(false)
      setTimeout(openPalette, 350)
      return
    }
    openPalette()
    return
  }
  dropdownOpen.value = !dropdownOpen.value
}

async function onSelect(g: GroupSummary): Promise<void> {
  dropdownOpen.value = false
  state.paletteOpen = false
  try {
    await switchGroup(Number(g.id), g.name)
  } catch (err: any) {
    const status = err && err.response && err.response.status
    if (status === 401 || status === 403) {
      proxy.$router.push('/forbidden')
      return
    }
    proxy.$notify({ type: 'danger', message: 'Could not switch to ' + g.name + '. Check your connection and try again.' })
  }
}

// The dropdown closes on a click anywhere outside it and its row.
function onDocMouseDown(e: MouseEvent): void {
  if (!dropdownOpen.value) return
  const t = e.target as Node | null
  if (!t) return
  if (trigger.value && trigger.value.contains(t)) return
  if (dropdown.value && dropdown.value.contains(t)) return
  dropdownOpen.value = false
}

async function onDocKey(e: KeyboardEvent): Promise<void> {
  if (e.key === 'Escape') {
    if (props.variant === 'sidebar' && dropdownOpen.value) await closeDropdown()
    if (props.variant === 'palette-host' && state.paletteOpen) closePalette()
    return
  }
  // leave Ctrl K to editors that use it (the email builder inserts a link with it)
  const target = e.target as HTMLElement | null
  if (e.defaultPrevented || (target && (target.isContentEditable || (target.closest && target.closest('.ql-editor'))))) return
  if (
    props.variant === 'palette-host' &&
    (e.metaKey || e.ctrlKey) &&
    !e.shiftKey &&
    !e.altKey &&
    String(e.key).toLowerCase() === 'k' &&
    canSwitch.value
  ) {
    e.preventDefault()
    if (state.paletteOpen) closePalette()
    else openPalette()
  }
}

onMounted(() => {
  document.addEventListener('keydown', onDocKey)
  if (props.variant === 'sidebar') document.addEventListener('mousedown', onDocMouseDown)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onDocKey)
  if (props.variant === 'sidebar') document.removeEventListener('mousedown', onDocMouseDown)
  if (props.variant === 'palette-host' && state.paletteOpen) document.body.style.overflow = prevOverflow
  const sidebar = trigger.value && trigger.value.closest('.sidebar')
  if (sidebar) sidebar.classList.remove('sidebar--picking')
})

return { __sfc: true,SIDEBAR_DRAWER_MAX, props, proxy, active, name, currentId, PLACEHOLDER_IMG, groupImg, initials, readJson, companyName, permissions, canManage, isMac, shortcutLabel, groups, canSwitch, switching, switchingToName, paletteOpen, trigger, dropdown, dropdownOpen, recentIds, isPhone, prevOverflow, prevFocus, openPalette, closePalette, closeDropdown, onBrandClick, onSelect, onDocMouseDown, onDocKey, GroupPicker }
}

})